<template>
  <!-- Connect calendar: a promotional "showcase" treatment with a gradient, a
       light sweep and drifting provider logos. Every other bar uses the plain
       layout below. -->
  <div v-if="showcase" role="region" aria-label="Account reminder" data-test="hellobar-showcase"
    class="relative isolate overflow-hidden flex items-start sm:items-center gap-3 sm:gap-4 px-4 sm:px-6 py-3 text-sm text-white border-b border-white/10 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 dark:from-blue-700 dark:via-indigo-700 dark:to-violet-800">

    <!-- Soft glows -->
    <div class="pointer-events-none absolute -top-12 left-[20%] w-56 h-24 rounded-full bg-white/20 blur-2xl -z-10" aria-hidden="true"></div>
    <div class="pointer-events-none absolute -bottom-14 right-[30%] w-64 h-24 rounded-full bg-fuchsia-400/25 blur-2xl -z-10" aria-hidden="true"></div>

    <!-- Drifting provider logos -->
    <div class="pointer-events-none absolute inset-0 -z-10" aria-hidden="true" data-test="hellobar-floaters">
      <span v-for="(item, index) in FLOATERS" :key="index"
        class="absolute items-center justify-center rounded-lg bg-white shadow-sm opacity-20 animate-hellobar-float motion-reduce:animate-none"
        :class="[item.tile, item.desktopOnly ? 'hidden sm:flex' : 'flex']"
        :style="{ left: item.left, top: item.top, animationDelay: item.delay, animationDuration: item.duration, '--hb-tilt': item.tilt }">
        <component :is="item.icon" :class="item.iconClass" />
      </span>
    </div>

    <!-- Light sweep -->
    <div class="pointer-events-none absolute inset-y-0 left-0 w-1/5 -z-10 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-hellobar-shine motion-reduce:hidden" aria-hidden="true"></div>

    <!-- Provider logos -->
    <div class="relative flex-shrink-0 w-[3.25rem] h-9" aria-hidden="true">
      <span class="absolute left-0 top-0 w-8 h-8 rounded-xl bg-white shadow-lg shadow-indigo-950/20 ring-1 ring-black/5 flex items-center justify-center -rotate-6">
        <GoogleIcon class="w-5 h-5" />
      </span>
      <span class="absolute left-5 top-1 w-8 h-8 rounded-xl bg-white shadow-lg shadow-indigo-950/20 ring-1 ring-black/5 flex items-center justify-center rotate-6">
        <OutlookIcon class="w-4 h-4" />
      </span>
    </div>

    <div class="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5 pt-0.5 sm:pt-0">
      <p class="min-w-0 leading-snug sm:truncate" :title="plainText">
        <span class="font-semibold text-white">{{ bar.title }}</span>
        <span class="hidden sm:inline mx-1.5 text-white/40" aria-hidden="true">·</span>
        <span class="sm:hidden"> </span>
        <span class="text-white/85">{{ bar.body }}</span>
      </p>

      <!-- data-turbo="false": see the note on the plain layout's CTA below. -->
      <a v-if="bar.cta" :href="bar.cta.url" data-turbo="false" data-test="hellobar-cta" @click="$emit('cta', $event)"
        class="group self-start sm:self-auto flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white text-indigo-700 text-xs font-bold whitespace-nowrap shadow-lg shadow-indigo-950/25 transition-all hover:bg-indigo-50 hover:-translate-y-px motion-reduce:hover:translate-y-0">
        {{ bar.cta.label }}
        <svg class="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 motion-reduce:group-hover:translate-x-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"/>
        </svg>
      </a>
    </div>

    <button v-if="bar.dismissible" type="button" aria-label="Dismiss" data-test="hellobar-dismiss" @click="$emit('dismiss')"
      class="flex-shrink-0 w-7 h-7 -mr-1 rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-colors">
      <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
      </svg>
    </button>
  </div>

  <div v-else role="region" :aria-label="preview ? 'Hellobar preview' : 'Account reminder'"
    class="flex items-start sm:items-center gap-3 px-4 sm:px-6 py-2.5 border-b text-sm"
    :class="tone.wrap">
    <span v-if="bar.emoji" data-test="hellobar-emoji"
      class="flex-shrink-0 w-7 h-7 flex items-center justify-center text-lg leading-none" aria-hidden="true">
      {{ bar.emoji }}
    </span>
    <span v-else class="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center" :class="tone.iconWrap">
      <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
        <path v-for="d in icon" :key="d" stroke-linecap="round" stroke-linejoin="round" :d="d"/>
      </svg>
    </span>

    <div class="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-4 pt-1 sm:pt-0">
      <p class="min-w-0 leading-snug sm:truncate" :title="plainText">
        <span v-if="preview" data-test="hellobar-preview-pill"
          class="inline-flex items-center mr-2 px-1.5 py-px rounded text-[10px] font-bold uppercase tracking-wide align-middle"
          :class="tone.pill">Preview</span>
        <span v-if="bar.title" class="font-semibold" :class="tone.title">{{ bar.title }}</span>
        <span v-if="bar.title" class="hidden sm:inline mx-1.5" :class="tone.muted" aria-hidden="true">·</span>
        <span v-if="bar.title" class="sm:hidden"> </span>
        <!-- Admin messages are rich text: sanitized by the server, and again here. -->
        <span v-if="safeHtml" data-test="hellobar-rich-body"
          class="[&_a]:font-semibold [&_a]:underline [&_a]:underline-offset-2"
          :class="tone.body" v-html="safeHtml"></span>
        <span v-else :class="tone.body">{{ bar.body }}</span>
      </p>

      <!-- data-turbo="false": a Turbo visit to a Vue Router page leaves the router
           on the previous route, which then restores the old URL. AppHellobar
           navigates instead (router push or full load). -->
      <a v-if="bar.cta" :href="bar.cta.url" data-turbo="false" data-test="hellobar-cta" @click="$emit('cta', $event)"
        class="self-start sm:self-auto flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold whitespace-nowrap transition-colors"
        :class="tone.cta">
        {{ bar.cta.label }}
        <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"/>
        </svg>
      </a>
    </div>

    <button v-if="bar.dismissible" type="button" :aria-label="preview ? 'Exit preview' : 'Dismiss'"
      :title="preview ? 'Exit preview' : undefined"
      data-test="hellobar-dismiss" @click="$emit('dismiss')"
      class="flex-shrink-0 w-7 h-7 -mr-1 rounded-lg flex items-center justify-center transition-colors"
      :class="tone.close">
      <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
      </svg>
    </button>
  </div>
</template>

<script setup>
import { computed, defineComponent, h } from 'vue'
import { GoogleIcon, OutlookIcon } from 'components/icons/calendar_provider_icons.js'
import { sanitizeHellobarHtml, hellobarPlainText } from 'controllers/vue_components/hellobar/richText.js'

// Presentational only, so the app bar, the on site preview and the admin page
// render the exact same markup. AppHellobar owns the API calls.
const props = defineProps({
  bar: { type: Object, required: true },
  preview: { type: Boolean, default: false },
})
defineEmits(['dismiss', 'cta'])

// One palette per AdminHellobar::COLORS preset. Text, icon, button and close
// colours are paired with each background so contrast holds in both themes.
const TONES = {
  blue: {
    wrap: 'bg-blue-50 border-blue-100 dark:bg-blue-500/10 dark:border-blue-500/20',
    iconWrap: 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-300',
    title: 'text-blue-950 dark:text-blue-50',
    body: 'text-blue-900/80 dark:text-blue-100/80',
    muted: 'text-blue-300 dark:text-blue-500/60',
    pill: 'bg-blue-600 text-white',
    cta: 'bg-blue-600 text-white hover:bg-blue-700',
    close: 'text-blue-400 hover:text-blue-700 hover:bg-blue-100 dark:text-blue-300/70 dark:hover:text-blue-100 dark:hover:bg-blue-500/20',
  },
  green: {
    wrap: 'bg-emerald-50 border-emerald-100 dark:bg-emerald-500/10 dark:border-emerald-500/20',
    iconWrap: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300',
    title: 'text-emerald-950 dark:text-emerald-50',
    body: 'text-emerald-900/80 dark:text-emerald-100/80',
    muted: 'text-emerald-300 dark:text-emerald-500/60',
    pill: 'bg-emerald-600 text-white',
    cta: 'bg-emerald-600 text-white hover:bg-emerald-700',
    close: 'text-emerald-500 hover:text-emerald-800 hover:bg-emerald-100 dark:text-emerald-300/70 dark:hover:text-emerald-100 dark:hover:bg-emerald-500/20',
  },
  amber: {
    wrap: 'bg-amber-50 border-amber-100 dark:bg-amber-500/10 dark:border-amber-500/20',
    iconWrap: 'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300',
    title: 'text-amber-950 dark:text-amber-50',
    body: 'text-amber-900/80 dark:text-amber-100/80',
    muted: 'text-amber-300 dark:text-amber-500/60',
    pill: 'bg-amber-500 text-white',
    cta: 'bg-amber-500 text-white hover:bg-amber-600',
    close: 'text-amber-500 hover:text-amber-800 hover:bg-amber-100 dark:text-amber-300/70 dark:hover:text-amber-100 dark:hover:bg-amber-500/20',
  },
  red: {
    wrap: 'bg-red-50 border-red-100 dark:bg-red-500/10 dark:border-red-500/20',
    iconWrap: 'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-300',
    title: 'text-red-950 dark:text-red-50',
    body: 'text-red-900/80 dark:text-red-100/80',
    muted: 'text-red-300 dark:text-red-500/60',
    pill: 'bg-red-600 text-white',
    cta: 'bg-red-600 text-white hover:bg-red-700',
    close: 'text-red-400 hover:text-red-700 hover:bg-red-100 dark:text-red-300/70 dark:hover:text-red-100 dark:hover:bg-red-500/20',
  },
  purple: {
    wrap: 'bg-violet-50 border-violet-100 dark:bg-violet-500/10 dark:border-violet-500/20',
    iconWrap: 'bg-violet-100 text-violet-600 dark:bg-violet-500/20 dark:text-violet-300',
    title: 'text-violet-950 dark:text-violet-50',
    body: 'text-violet-900/80 dark:text-violet-100/80',
    muted: 'text-violet-300 dark:text-violet-500/60',
    pill: 'bg-violet-600 text-white',
    cta: 'bg-violet-600 text-white hover:bg-violet-700',
    close: 'text-violet-400 hover:text-violet-700 hover:bg-violet-100 dark:text-violet-300/70 dark:hover:text-violet-100 dark:hover:bg-violet-500/20',
  },
  gray: {
    wrap: 'bg-gray-100 border-gray-200 dark:bg-white/5 dark:border-white/10',
    iconWrap: 'bg-gray-200 text-gray-600 dark:bg-white/10 dark:text-gray-300',
    title: 'text-gray-900 dark:text-white',
    body: 'text-gray-700 dark:text-gray-300',
    muted: 'text-gray-400 dark:text-gray-600',
    pill: 'bg-gray-700 text-white',
    cta: 'bg-gray-800 text-white hover:bg-gray-900 dark:bg-white/15 dark:hover:bg-white/25',
    close: 'text-gray-400 hover:text-gray-700 hover:bg-gray-200 dark:text-gray-400 dark:hover:text-white dark:hover:bg-white/10',
  },
  dark: {
    wrap: 'bg-gray-900 border-gray-800 dark:bg-black dark:border-white/10',
    iconWrap: 'bg-white/10 text-white',
    title: 'text-white',
    body: 'text-gray-200',
    muted: 'text-gray-500',
    pill: 'bg-white text-gray-900',
    cta: 'bg-white text-gray-900 hover:bg-gray-100',
    close: 'text-gray-400 hover:text-white hover:bg-white/10',
  },
}

// Predefined bars still say "info" and "warning".
const TONE_ALIASES = { info: 'blue', warning: 'amber' }

const ICONS = {
  megaphone: 'M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 1 1 0-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 0 1-1.44-4.282m3.102.069a18.03 18.03 0 0 1-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 0 1 8.835 2.535M10.34 6.66a23.847 23.847 0 0 0 8.835-2.535m0 0A23.74 23.74 0 0 0 18.795 3m.38 1.125a23.91 23.91 0 0 1 1.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 0 0 1.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 0 1 0 3.46',
  calendar: 'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5',
  shield: 'M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z',
}

// A plain calendar glyph to mix in with the brand logos in the background.
const CalendarGlyph = defineComponent({
  props: { class: String },
  render() {
    return h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: '#4f46e5', 'stroke-width': '1.8', class: this.class }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', d: ICONS.calendar }),
    ])
  },
})

// Background logos for the connect calendar bar. Negative delays start each one
// part way through its drift, so they never move in unison. Half are hidden on
// mobile, where the text needs the room.
const FLOATERS = [
  { icon: OutlookIcon,   left: '26%', top: '54%', tilt: '-6deg',  delay: '-4.5s', duration: '9.5s', tile: 'w-6 h-6', iconClass: 'w-3.5 h-3.5', desktopOnly: true },
  { icon: GoogleIcon,    left: '37%', top: '8%',  tilt: '-12deg', delay: '0s',    duration: '7s',   tile: 'w-7 h-7', iconClass: 'w-5 h-5' },
  { icon: CalendarGlyph, left: '47%', top: '50%', tilt: '10deg',  delay: '-2s',   duration: '8s',   tile: 'w-6 h-6', iconClass: 'w-4 h-4', desktopOnly: true },
  { icon: OutlookIcon,   left: '57%', top: '10%', tilt: '6deg',   delay: '-4s',   duration: '6.5s', tile: 'w-6 h-6', iconClass: 'w-3.5 h-3.5' },
  { icon: GoogleIcon,    left: '66%', top: '52%', tilt: '14deg',  delay: '-1s',   duration: '9s',   tile: 'w-7 h-7', iconClass: 'w-5 h-5', desktopOnly: true },
  { icon: CalendarGlyph, left: '75%', top: '8%',  tilt: '-8deg',  delay: '-3s',   duration: '7.5s', tile: 'w-6 h-6', iconClass: 'w-4 h-4' },
  { icon: OutlookIcon,   left: '83%', top: '48%', tilt: '-14deg', delay: '-5s',   duration: '8.5s', tile: 'w-6 h-6', iconClass: 'w-3.5 h-3.5', desktopOnly: true },
  { icon: GoogleIcon,    left: '91%', top: '6%',  tilt: '8deg',   delay: '-2.5s', duration: '6s',   tile: 'w-7 h-7', iconClass: 'w-5 h-5' },
]

// Only the predefined connect calendar bar gets the showcase treatment.
const showcase = computed(() => props.bar.key === 'connect_calendar' && !props.preview)

const tone = computed(() => TONES[TONE_ALIASES[props.bar.tone] || props.bar.tone] || TONES.blue)
const icon = computed(() => [ICONS[props.bar.icon] || ICONS.megaphone])
const safeHtml = computed(() => (props.bar.body_html ? sanitizeHellobarHtml(props.bar.body_html) : ''))
const plainText = computed(() => {
  const body = props.bar.body_html ? hellobarPlainText(props.bar.body_html) : props.bar.body
  return [props.bar.title, body].filter(Boolean).join('. ')
})
</script>
